import { useCallback, useEffect, useRef, useState } from "react";

const INK = "#2b1d12";
const INK_SOFT = "#4a3a2a";
const GOLD = "#8a6a3b";
const PARCHMENT = "#faf5ec";
const RULE = "#e3d6bf";

const NAME_KEY = "queen-farah:chat:name";
const SECRET_SEQUENCE = "farah";
const POLL_INTERVAL_MS = 2500;
const MAX_TEXT_LEN = 2000;

type ChatMessage = {
  id: string;
  name: string;
  text: string;
  ts: number;
};

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function SecretChat() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const lastTsRef = useRef(0);
  const seenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    const saved = localStorage.getItem(NAME_KEY);
    if (saved) setName(saved);
  }, []);

  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#chat") setOpen(true);
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    const onOpen = () => setOpen(true);
    window.addEventListener("secret-chat:open", onOpen);
    return () => {
      window.removeEventListener("hashchange", checkHash);
      window.removeEventListener("secret-chat:open", onOpen);
    };
  }, []);

  useEffect(() => {
    let buffer = "";
    let clearTimer: ReturnType<typeof setTimeout> | null = null;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (e.key.length !== 1) return;
      buffer = (buffer + e.key.toLowerCase()).slice(-SECRET_SEQUENCE.length);
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(() => {
        buffer = "";
      }, 2000);
      if (buffer === SECRET_SEQUENCE) {
        buffer = "";
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (clearTimer) clearTimeout(clearTimer);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };

  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!name) {
      nameInputRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [open, name]);

  const mergeMessages = useCallback((incoming: ChatMessage[]) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const seen = seenIdsRef.current;
      const next = [...prev];
      for (const m of incoming) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        next.push(m);
        if (m.ts > lastTsRef.current) lastTsRef.current = m.ts;
      }
      next.sort((a, b) => a.ts - b.ts);
      return next;
    });
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (lastTsRef.current > 0) params.set("since", String(lastTsRef.current));
      if (
        name &&
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        params.set("name", name);
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(`/api/chat${qs}`, { cache: "no-store" });
      if (!res.ok) {
        setError("Could not reach the scroll.");
        return;
      }
      const data = (await res.json()) as { messages?: ChatMessage[] };
      if (Array.isArray(data.messages)) {
        mergeMessages(data.messages);
        setError(null);
      }
    } catch {
      setError("Could not reach the scroll.");
    }
  }, [mergeMessages, name]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      await fetchMessages();
      if (!cancelled) setLoading(false);
    })();
    const id = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, fetchMessages]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const handleClose = () => {
    setOpen(false);
    if (window.location.hash === "#chat") {
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  };

  const handleSaveName = () => {
    const trimmed = nameInput.trim().slice(0, 40);
    if (!trimmed) return;
    try {
      localStorage.setItem(NAME_KEY, trimmed);
    } catch {}
    setName(trimmed);
    setNameInput("");
  };

  const handleChangeName = () => {
    try {
      localStorage.removeItem(NAME_KEY);
    } catch {}
    setNameInput(name);
    setName("");
  };

  const handleSend = async () => {
    const text = draft.trim().slice(0, MAX_TEXT_LEN);
    if (!text || !name || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, text }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setError(data.error || "Message did not send.");
      } else {
        const data = (await res.json()) as { message?: ChatMessage };
        if (data.message) mergeMessages([data.message]);
        setDraft("");
      }
    } catch {
      setError("Message did not send.");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 font-serif"
      style={{ backgroundColor: "rgba(43, 29, 18, 0.55)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chat-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="w-full max-w-xl border shadow-2xl flex flex-col"
        style={{
          backgroundColor: PARCHMENT,
          borderColor: RULE,
          color: INK,
          height: "min(88svh, 720px)",
        }}
      >
        <div
          className="flex items-center justify-between px-5 sm:px-7 py-4 border-b"
          style={{ borderColor: RULE }}
        >
          <div>
            <div
              className="text-[10px] sm:text-xs tracking-[0.3em] uppercase"
              style={{ color: GOLD }}
            >
              A Private Scroll
            </div>
            <h2
              id="chat-title"
              className="text-xl sm:text-2xl italic text-pretty"
              style={{ color: INK }}
            >
              Whispers of the Court
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="text-[10px] tracking-[0.3em] uppercase border px-3 py-2 hover:bg-current hover:text-white transition-colors"
            style={{ color: INK, borderColor: RULE }}
          >
            Dismiss
          </button>
        </div>

        {!name ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-10 text-center">
            <div
              className="text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-3"
              style={{ color: GOLD }}
            >
              Identify thyself
            </div>
            <p
              className="text-base leading-relaxed italic mb-6 text-pretty"
              style={{ color: INK_SOFT }}
            >
              Before entering the scroll, declare the name by which you wish to
              be known.
            </p>
            <input
              ref={nameInputRef}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && nameInput.trim()) handleSaveName();
              }}
              maxLength={40}
              autoComplete="off"
              spellCheck={false}
              className="w-full max-w-sm px-4 py-3 text-center italic border bg-transparent focus:outline-none focus:border-current"
              style={{ borderColor: RULE, color: INK, fontFamily: "inherit" }}
              placeholder="your name"
              aria-label="Your name"
            />
            <button
              type="button"
              disabled={!nameInput.trim()}
              onClick={handleSaveName}
              className="mt-5 px-6 py-3 text-[11px] tracking-[0.3em] uppercase border transition-colors"
              style={{
                borderColor: nameInput.trim() ? INK : RULE,
                color: nameInput.trim() ? PARCHMENT : GOLD,
                backgroundColor: nameInput.trim() ? INK : "transparent",
                cursor: nameInput.trim() ? "pointer" : "not-allowed",
                opacity: nameInput.trim() ? 1 : 0.7,
              }}
            >
              Enter the scroll
            </button>
          </div>
        ) : (
          <>
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-4"
            >
              {loading && messages.length === 0 && (
                <div
                  className="text-center text-sm italic"
                  style={{ color: INK_SOFT, opacity: 0.7 }}
                >
                  Unfurling the scroll…
                </div>
              )}
              {!loading && messages.length === 0 && (
                <div
                  className="text-center text-sm italic"
                  style={{ color: INK_SOFT, opacity: 0.7 }}
                >
                  The scroll is blank. Leave the first word.
                </div>
              )}
              {messages.map((m) => {
                const mine = m.name === name;
                return (
                  <div
                    key={m.id}
                    className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
                  >
                    <div
                      className="flex items-baseline gap-2 text-[10px] tracking-[0.2em] uppercase mb-1"
                      style={{ color: GOLD }}
                    >
                      <span>{m.name}</span>
                      <span style={{ opacity: 0.6 }}>{formatTime(m.ts)}</span>
                    </div>
                    <div
                      className="max-w-[85%] px-4 py-2 text-base leading-relaxed whitespace-pre-wrap break-words border"
                      style={{
                        color: mine ? PARCHMENT : INK,
                        backgroundColor: mine ? INK : "transparent",
                        borderColor: mine ? INK : RULE,
                      }}
                    >
                      {m.text}
                    </div>
                  </div>
                );
              })}
            </div>

            {error && (
              <div
                className="px-5 sm:px-7 py-2 text-[11px] italic text-center border-t"
                style={{ color: GOLD, borderColor: RULE }}
              >
                {error}
              </div>
            )}

            <div
              className="border-t px-5 sm:px-7 py-4 flex flex-col gap-3"
              style={{ borderColor: RULE }}
            >
              <div className="flex items-end gap-3">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  rows={1}
                  maxLength={MAX_TEXT_LEN}
                  placeholder="leave a whisper…"
                  aria-label="Your message"
                  className="flex-1 resize-none px-3 py-2 text-base leading-relaxed border bg-transparent focus:outline-none focus:border-current"
                  style={{
                    borderColor: RULE,
                    color: INK,
                    fontFamily: "inherit",
                    maxHeight: "8rem",
                  }}
                />
                <button
                  type="button"
                  disabled={!draft.trim() || sending}
                  onClick={handleSend}
                  className="px-4 py-3 text-[11px] tracking-[0.3em] uppercase border transition-colors"
                  style={{
                    borderColor: draft.trim() && !sending ? INK : RULE,
                    color: draft.trim() && !sending ? PARCHMENT : GOLD,
                    backgroundColor:
                      draft.trim() && !sending ? INK : "transparent",
                    cursor: draft.trim() && !sending ? "pointer" : "not-allowed",
                    opacity: draft.trim() && !sending ? 1 : 0.7,
                  }}
                >
                  {sending ? "Sending" : "Send"}
                </button>
              </div>
              <div
                className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase"
                style={{ color: GOLD }}
              >
                <span>Signed as {name}</span>
                <button
                  type="button"
                  onClick={handleChangeName}
                  className="underline hover:no-underline"
                  style={{ color: GOLD }}
                >
                  Change name
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
